import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_URL } from "../config";

/**
 * INTALK Platform API (Phase 1 — multi-tenant foundation).
 * Companies, team users, roles/permissions, communication numbers,
 * provider settings, API clients and the audit log.
 *
 * Super admins can scope a view to a company by setting localStorage
 * "intalk_company_id" (sent as the X-Company-Id header).
 */
export const platformApi = createApi({
  reducerPath: "platformApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      headers.set("Authorization", `Bearer ${localStorage.getItem("token") || ""}`);
      // A per-request X-Company-Id (set by company-scoped endpoints, e.g. the
      // Company Control Center) always wins over the global localStorage scope.
      const companyId = localStorage.getItem("intalk_company_id");
      if (companyId && !headers.has("X-Company-Id")) headers.set("X-Company-Id", companyId);
      return headers;
    },
  }),
  tagTypes: [
    "Company",
    "CompanyOverview",
    "CompanyUser",
    "Role",
    "Permission",
    "Number",
    "Provider",
    "ApiClient",
    "ActivityLog",
    "SmsMessage",
    "WhatsApp",
    "InboxConversation",
    "InboxThread",
    "Webhook",
    "Health",
    "Template",
    "BillingPlan",
    "BillingSubscription",
    "BillingInvoice",
    "BillingCard",
    "BillingPayment",
    "Documentation",
    "DocArticle",
    "Call",
    "Voicemail",
  ],
  endpoints: (builder) => ({
    // ---- Companies ----
    getCompanies: builder.query<any, void>({
      query: () => "platform/companies",
      providesTags: ["Company"],
    }),
    createCompany: builder.mutation<any, any>({
      query: (body) => ({ url: "platform/companies", method: "POST", body }),
      invalidatesTags: ["Company"],
    }),
    updateCompany: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/companies/${id}`, method: "PUT", body }),
      invalidatesTags: ["Company"],
    }),
    deleteCompany: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/companies/${id}`, method: "DELETE" }),
      invalidatesTags: ["Company"],
    }),

    // ---- Company Control Center (tenant profile) ----
    // Aggregated read for the /companies/:id page; every sub-resource write
    // below scopes to that company via the X-Company-Id header and invalidates
    // CompanyOverview so the page refetches.
    getCompanyOverview: builder.query<any, number>({
      query: (id) => `platform/companies/${id}/overview`,
      providesTags: ["CompanyOverview"],
    }),
    updateCompanyProfile: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/companies/${id}`, method: "PUT", body }),
      invalidatesTags: ["CompanyOverview", "Company"],
    }),
    updateCompanyBranding: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/companies/${id}/branding`, method: "PUT", body }),
      invalidatesTags: ["CompanyOverview", "Company"],
    }),
    updateCompanySecurity: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/companies/${id}/security`, method: "PUT", body }),
      invalidatesTags: ["CompanyOverview"],
    }),

    // ---- Integrations (company-scoped) ----
    createIntegration: builder.mutation<any, { companyId: number; body: any }>({
      query: ({ companyId, body }) => ({
        url: "platform/integrations", method: "POST", body,
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),
    updateIntegration: builder.mutation<any, { companyId: number; id: number; body: any }>({
      query: ({ companyId, id, body }) => ({
        url: `platform/integrations/${id}`, method: "PUT", body,
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),
    deleteIntegration: builder.mutation<any, { companyId: number; id: number }>({
      query: ({ companyId, id }) => ({
        url: `platform/integrations/${id}`, method: "DELETE",
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),

    // ---- Numbers (company-scoped) ----
    createNumberFor: builder.mutation<any, { companyId: number; body: any }>({
      query: ({ companyId, body }) => ({
        url: "platform/numbers", method: "POST", body,
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),
    deleteNumberFor: builder.mutation<any, { companyId: number; id: number }>({
      query: ({ companyId, id }) => ({
        url: `platform/numbers/${id}`, method: "DELETE",
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),

    // ---- Team users (company-scoped) ----
    createUserFor: builder.mutation<any, { companyId: number; body: any }>({
      query: ({ companyId, body }) => ({
        url: "platform/users", method: "POST", body,
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),
    updateUserFor: builder.mutation<any, { companyId: number; id: number; body: any }>({
      query: ({ companyId, id, body }) => ({
        url: `platform/users/${id}`, method: "PUT", body,
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),
    deleteUserFor: builder.mutation<any, { companyId: number; id: number }>({
      query: ({ companyId, id }) => ({
        url: `platform/users/${id}`, method: "DELETE",
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),

    // ---- API clients (company-scoped) ----
    createApiClientFor: builder.mutation<any, { companyId: number; body: any }>({
      query: ({ companyId, body }) => ({
        url: "platform/api-clients", method: "POST", body,
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),
    revokeApiClientFor: builder.mutation<any, { companyId: number; id: number }>({
      query: ({ companyId, id }) => ({
        url: `platform/api-clients/${id}/revoke`, method: "POST",
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),
    regenerateApiClientFor: builder.mutation<any, { companyId: number; id: number }>({
      query: ({ companyId, id }) => ({
        url: `platform/api-clients/${id}/regenerate`, method: "POST",
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),
    deleteApiClientFor: builder.mutation<any, { companyId: number; id: number }>({
      query: ({ companyId, id }) => ({
        url: `platform/api-clients/${id}`, method: "DELETE",
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),

    // ---- Subscription (company-scoped) ----
    assignSubscriptionFor: builder.mutation<any, { companyId: number; body: any }>({
      query: ({ companyId, body }) => ({
        url: "platform/billing/subscription", method: "POST",
        body: { ...body, company_id: companyId },
        headers: { "X-Company-Id": String(companyId) },
      }),
      invalidatesTags: ["CompanyOverview"],
    }),

    // ---- Team users ----
    getCompanyUsers: builder.query<any, void>({
      query: () => "platform/users",
      providesTags: ["CompanyUser"],
    }),
    createCompanyUser: builder.mutation<any, any>({
      query: (body) => ({ url: "platform/users", method: "POST", body }),
      invalidatesTags: ["CompanyUser"],
    }),
    updateCompanyUser: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/users/${id}`, method: "PUT", body }),
      invalidatesTags: ["CompanyUser"],
    }),
    deleteCompanyUser: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/users/${id}`, method: "DELETE" }),
      invalidatesTags: ["CompanyUser"],
    }),

    // ---- Roles & permissions ----
    getRoles: builder.query<any, void>({
      query: () => "platform/roles",
      providesTags: ["Role"],
    }),
    createRole: builder.mutation<any, any>({
      query: (body) => ({ url: "platform/roles", method: "POST", body }),
      invalidatesTags: ["Role"],
    }),
    updateRole: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/roles/${id}`, method: "PUT", body }),
      invalidatesTags: ["Role"],
    }),
    deleteRole: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/roles/${id}`, method: "DELETE" }),
      invalidatesTags: ["Role"],
    }),
    getPermissions: builder.query<any, void>({
      query: () => "platform/permissions",
      providesTags: ["Permission"],
    }),

    // ---- Communication numbers ----
    getNumbers: builder.query<any, void>({
      query: () => "platform/numbers",
      providesTags: ["Number"],
    }),
    createNumber: builder.mutation<any, any>({
      query: (body) => ({ url: "platform/numbers", method: "POST", body }),
      invalidatesTags: ["Number"],
    }),
    updateNumber: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/numbers/${id}`, method: "PUT", body }),
      invalidatesTags: ["Number"],
    }),
    deleteNumber: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/numbers/${id}`, method: "DELETE" }),
      invalidatesTags: ["Number"],
    }),
    testNumberSms: builder.mutation<any, { id: number; body: { to: string; message: string } }>({
      query: ({ id, body }) => ({ url: `platform/numbers/${id}/test-sms`, method: "POST", body }),
      invalidatesTags: ["SmsMessage"],
    }),

    // ---- Providers ----
    getProviders: builder.query<any, void>({
      query: () => "platform/providers",
      providesTags: ["Provider"],
    }),
    // companyId scopes the write to a tenant (super admin selects it; company
    // admins may omit it and the backend resolves their own company). Sent both
    // as the X-Company-Id header and company_id in the body, like createNumberFor.
    createProvider: builder.mutation<any, { companyId?: number; body: any }>({
      query: ({ companyId, body }) => ({
        url: "platform/providers",
        method: "POST",
        body: companyId ? { ...body, company_id: companyId } : body,
        ...(companyId ? { headers: { "X-Company-Id": String(companyId) } } : {}),
      }),
      invalidatesTags: ["Provider"],
    }),
    updateProvider: builder.mutation<any, { id: number; companyId?: number; body: any }>({
      query: ({ id, companyId, body }) => ({
        url: `platform/providers/${id}`,
        method: "PUT",
        body,
        ...(companyId ? { headers: { "X-Company-Id": String(companyId) } } : {}),
      }),
      invalidatesTags: ["Provider"],
    }),
    deleteProvider: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/providers/${id}`, method: "DELETE" }),
      invalidatesTags: ["Provider"],
    }),

    // ---- API clients ----
    getApiClients: builder.query<any, void>({
      query: () => "platform/api-clients",
      providesTags: ["ApiClient"],
    }),
    createApiClient: builder.mutation<any, any>({
      query: (body) => ({ url: "platform/api-clients", method: "POST", body }),
      invalidatesTags: ["ApiClient"],
    }),
    revokeApiClient: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/api-clients/${id}/revoke`, method: "POST" }),
      invalidatesTags: ["ApiClient"],
    }),
    regenerateApiClient: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/api-clients/${id}/regenerate`, method: "POST" }),
      invalidatesTags: ["ApiClient"],
    }),
    getApiClientUsage: builder.query<any, number>({
      query: (id) => `platform/api-clients/${id}/usage`,
    }),
    deleteApiClient: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/api-clients/${id}`, method: "DELETE" }),
      invalidatesTags: ["ApiClient"],
    }),

    // ---- Webhook subscriptions ----
    getWebhooks: builder.query<any, void>({
      query: () => "platform/webhooks",
      providesTags: ["Webhook"],
    }),
    createWebhook: builder.mutation<any, any>({
      query: (body) => ({ url: "platform/webhooks", method: "POST", body }),
      invalidatesTags: ["Webhook"],
    }),
    updateWebhook: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/webhooks/${id}`, method: "PUT", body }),
      invalidatesTags: ["Webhook"],
    }),
    deleteWebhook: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/webhooks/${id}`, method: "DELETE" }),
      invalidatesTags: ["Webhook"],
    }),

    // ---- System health / failed jobs / usage (Phase 5) ----
    getHealth: builder.query<any, void>({
      query: () => "platform/health",
      providesTags: ["Health"],
    }),
    getFailedJobs: builder.query<any, void>({
      query: () => "platform/failed-jobs",
      providesTags: ["Health"],
    }),
    retryFailedJob: builder.mutation<any, string>({
      query: (uuid) => ({ url: `platform/failed-jobs/${uuid}/retry`, method: "POST" }),
      invalidatesTags: ["Health"],
    }),
    getUsageSummary: builder.query<any, void>({
      query: () => "platform/usage",
    }),

    // ---- Activity log ----
    getActivityLogs: builder.query<any, void>({
      query: () => "platform/activity-logs",
      providesTags: ["ActivityLog"],
    }),

    // ---- SMS engine (Phase 2) ----
    getSmsMessages: builder.query<any, { direction?: string; status?: string; search?: string } | void>({
      query: (params) => {
        const qs = new URLSearchParams(
          Object.entries(params || {}).filter(([, v]) => v) as [string, string][]
        ).toString();
        return `sms/messages${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["SmsMessage"],
    }),
    sendSms: builder.mutation<any, { number_id?: number; to: string; message: string }>({
      query: (body) => ({ url: "sms/send", method: "POST", body }),
      invalidatesTags: ["SmsMessage"],
    }),
    sendTestSms: builder.mutation<any, { number_id?: number; to: string; message?: string }>({
      query: (body) => ({ url: "sms/test", method: "POST", body }),
      invalidatesTags: ["SmsMessage"],
    }),

    // ---- WhatsApp (Phase 2.5) ----
    getWhatsappSettings: builder.query<any, void>({
      query: () => "whatsapp/settings",
      providesTags: ["WhatsApp"],
    }),
    getWhatsappMessages: builder.query<any, void>({
      query: () => "whatsapp/messages",
      providesTags: ["WhatsApp"],
    }),
    sendTestWhatsapp: builder.mutation<any, { number_id?: number; to: string; message?: string }>({
      query: (body) => ({ url: "whatsapp/test", method: "POST", body }),
      invalidatesTags: ["WhatsApp"],
    }),

    // ---- Unified Inbox (Phase 3) ----
    getInboxConversations: builder.query<any, Record<string, any> | void>({
      query: (params) => {
        const qs = new URLSearchParams(
          Object.entries(params || {}).filter(([, v]) => v !== "" && v != null) as [string, string][]
        ).toString();
        return `inbox/conversations${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["InboxConversation"],
    }),
    getInboxConversation: builder.query<any, number>({
      query: (id) => `inbox/conversations/${id}`,
      providesTags: ["InboxThread"],
    }),
    getInboxUnread: builder.query<any, void>({
      query: () => "inbox/unread-count",
      providesTags: ["InboxConversation"],
    }),
    replyInbox: builder.mutation<any, { id: number; channel: string; message: string }>({
      query: ({ id, ...body }) => ({ url: `inbox/${id}/reply`, method: "POST", body }),
      invalidatesTags: ["InboxThread", "InboxConversation"],
    }),
    assignInbox: builder.mutation<any, { id: number; assigned_user_id: number | null }>({
      query: ({ id, ...body }) => ({ url: `inbox/${id}/assign`, method: "PUT", body }),
      invalidatesTags: ["InboxThread", "InboxConversation"],
    }),
    setInboxStatus: builder.mutation<any, { id: number; status: string }>({
      query: ({ id, ...body }) => ({ url: `inbox/${id}/status`, method: "PUT", body }),
      invalidatesTags: ["InboxThread", "InboxConversation"],
    }),
    addInboxNote: builder.mutation<any, { id: number; note: string }>({
      query: ({ id, ...body }) => ({ url: `inbox/${id}/notes`, method: "POST", body }),
      invalidatesTags: ["InboxThread"],
    }),
    addInboxTag: builder.mutation<any, { id: number; tag: string }>({
      query: ({ id, ...body }) => ({ url: `inbox/${id}/tags`, method: "POST", body }),
      invalidatesTags: ["InboxThread", "InboxConversation"],
    }),
    removeInboxTag: builder.mutation<any, { id: number; tagId: number }>({
      query: ({ id, tagId }) => ({ url: `inbox/${id}/tags/${tagId}`, method: "DELETE" }),
      invalidatesTags: ["InboxThread", "InboxConversation"],
    }),

    // ---- Communication Templates (Phase 6) ----
    getTemplates: builder.query<any, { channel?: string; category?: string } | void>({
      query: (params) => {
        const qs = new URLSearchParams(Object.entries(params || {}).filter(([, v]) => v) as [string, string][]).toString();
        return `platform/templates${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["Template"],
    }),
    getTemplateVariables: builder.query<any, void>({
      query: () => "platform/template-variables",
    }),
    createTemplate: builder.mutation<any, any>({
      query: (body) => ({ url: "platform/templates", method: "POST", body }),
      invalidatesTags: ["Template"],
    }),
    updateTemplate: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/templates/${id}`, method: "PUT", body }),
      invalidatesTags: ["Template"],
    }),
    deleteTemplate: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/templates/${id}`, method: "DELETE" }),
      invalidatesTags: ["Template"],
    }),
    previewTemplate: builder.mutation<any, { subject?: string; body: string; data?: any }>({
      query: (body) => ({ url: "platform/templates/preview", method: "POST", body }),
    }),
    aiGenerateTemplate: builder.mutation<any, { prompt: string; channel: string }>({
      query: (body) => ({ url: "platform/templates/ai-generate", method: "POST", body }),
    }),

    // ---- Billing (Phase 7) ----
    getBillingDashboard: builder.query<any, void>({ query: () => "platform/billing/dashboard", providesTags: ["BillingSubscription"] }),
    getPlans: builder.query<any, void>({ query: () => "platform/billing/plans", providesTags: ["BillingPlan"] }),
    createPlan: builder.mutation<any, any>({ query: (body) => ({ url: "platform/billing/plans", method: "POST", body }), invalidatesTags: ["BillingPlan"] }),
    updatePlan: builder.mutation<any, { id: number; body: any }>({ query: ({ id, body }) => ({ url: `platform/billing/plans/${id}`, method: "PUT", body }), invalidatesTags: ["BillingPlan"] }),
    deletePlan: builder.mutation<any, number>({ query: (id) => ({ url: `platform/billing/plans/${id}`, method: "DELETE" }), invalidatesTags: ["BillingPlan"] }),

    getSubscription: builder.query<any, void>({ query: () => "platform/billing/subscription", providesTags: ["BillingSubscription"] }),
    assignSubscription: builder.mutation<any, any>({ query: (body) => ({ url: "platform/billing/subscription", method: "POST", body }), invalidatesTags: ["BillingSubscription"] }),

    getInvoices: builder.query<any, void>({ query: () => "platform/billing/invoices", providesTags: ["BillingInvoice"] }),
    getInvoice: builder.query<any, number>({ query: (id) => `platform/billing/invoices/${id}` }),
    payInvoice: builder.mutation<any, number>({ query: (id) => ({ url: `platform/billing/invoices/${id}/pay`, method: "POST" }), invalidatesTags: ["BillingInvoice", "BillingPayment"] }),

    getPaymentProfiles: builder.query<any, void>({ query: () => "platform/billing/payment-profiles", providesTags: ["BillingCard"] }),
    addPaymentProfile: builder.mutation<any, any>({ query: (body) => ({ url: "platform/billing/payment-profiles", method: "POST", body }), invalidatesTags: ["BillingCard"] }),
    setDefaultCard: builder.mutation<any, number>({ query: (id) => ({ url: `platform/billing/payment-profiles/${id}/default`, method: "POST" }), invalidatesTags: ["BillingCard"] }),
    removeCard: builder.mutation<any, number>({ query: (id) => ({ url: `platform/billing/payment-profiles/${id}`, method: "DELETE" }), invalidatesTags: ["BillingCard"] }),

    getPayments: builder.query<any, void>({ query: () => "platform/billing/payments", providesTags: ["BillingPayment"] }),
    manualPayment: builder.mutation<any, any>({ query: (body) => ({ url: "platform/billing/payments/manual", method: "POST", body }), invalidatesTags: ["BillingPayment", "BillingInvoice"] }),

    // ---- Documentation & Onboarding Center (Phase 8.5) ----
    getDocumentation: builder.query<any, void>({
      query: () => "platform/documentation",
      providesTags: ["Documentation"],
    }),
    getDocArticle: builder.query<any, number>({
      query: (id) => `platform/documentation/articles/${id}`,
      providesTags: (_r, _e, id) => [{ type: "DocArticle", id }],
    }),
    getOnboarding: builder.query<any, void>({
      query: () => "platform/documentation/onboarding",
    }),
    createDocCategory: builder.mutation<any, any>({
      query: (body) => ({ url: "platform/documentation/categories", method: "POST", body }),
      invalidatesTags: ["Documentation"],
    }),
    updateDocCategory: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/documentation/categories/${id}`, method: "PUT", body }),
      invalidatesTags: ["Documentation"],
    }),
    deleteDocCategory: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/documentation/categories/${id}`, method: "DELETE" }),
      invalidatesTags: ["Documentation"],
    }),
    createDocArticle: builder.mutation<any, any>({
      query: (body) => ({ url: "platform/documentation/articles", method: "POST", body }),
      invalidatesTags: ["Documentation"],
    }),
    updateDocArticle: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `platform/documentation/articles/${id}`, method: "PUT", body }),
      invalidatesTags: (_r, _e, { id }) => ["Documentation", { type: "DocArticle", id }],
    }),
    deleteDocArticle: builder.mutation<any, number>({
      query: (id) => ({ url: `platform/documentation/articles/${id}`, method: "DELETE" }),
      invalidatesTags: ["Documentation"],
    }),

    // ---- Voice (Phase 9) ----
    getCalls: builder.query<any, { direction?: string; status?: string; search?: string } | void>({
      query: (params) => ({ url: "voice/calls", params: params || undefined }),
      providesTags: ["Call"],
    }),
    getCall: builder.query<any, number>({
      query: (id) => `voice/calls/${id}`,
    }),
    placeCall: builder.mutation<any, { to: string; number_id?: number; customer_name?: string }>({
      query: (body) => ({ url: "voice/calls", method: "POST", body }),
      invalidatesTags: ["Call"],
    }),
    getVoicemails: builder.query<any, { unread_only?: boolean } | void>({
      query: (params) => ({ url: "voice/voicemails", params: params || undefined }),
      providesTags: ["Voicemail"],
    }),
    markVoicemailRead: builder.mutation<any, number>({
      query: (id) => ({ url: `voice/voicemails/${id}/read`, method: "POST" }),
      invalidatesTags: ["Voicemail"],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useGetCompanyOverviewQuery,
  useUpdateCompanyProfileMutation,
  useUpdateCompanyBrandingMutation,
  useUpdateCompanySecurityMutation,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useCreateNumberForMutation,
  useDeleteNumberForMutation,
  useCreateUserForMutation,
  useUpdateUserForMutation,
  useDeleteUserForMutation,
  useCreateApiClientForMutation,
  useRevokeApiClientForMutation,
  useRegenerateApiClientForMutation,
  useDeleteApiClientForMutation,
  useAssignSubscriptionForMutation,
  useGetCompanyUsersQuery,
  useCreateCompanyUserMutation,
  useUpdateCompanyUserMutation,
  useDeleteCompanyUserMutation,
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetPermissionsQuery,
  useGetNumbersQuery,
  useTestNumberSmsMutation,
  useCreateNumberMutation,
  useUpdateNumberMutation,
  useDeleteNumberMutation,
  useGetProvidersQuery,
  useCreateProviderMutation,
  useUpdateProviderMutation,
  useDeleteProviderMutation,
  useGetApiClientsQuery,
  useCreateApiClientMutation,
  useRevokeApiClientMutation,
  useRegenerateApiClientMutation,
  useDeleteApiClientMutation,
  useGetWebhooksQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useGetHealthQuery,
  useGetFailedJobsQuery,
  useRetryFailedJobMutation,
  useGetUsageSummaryQuery,
  useGetActivityLogsQuery,
  useGetSmsMessagesQuery,
  useSendSmsMutation,
  useSendTestSmsMutation,
  useGetWhatsappSettingsQuery,
  useGetWhatsappMessagesQuery,
  useSendTestWhatsappMutation,
  useGetInboxConversationsQuery,
  useGetInboxConversationQuery,
  useGetInboxUnreadQuery,
  useReplyInboxMutation,
  useAssignInboxMutation,
  useSetInboxStatusMutation,
  useAddInboxNoteMutation,
  useAddInboxTagMutation,
  useRemoveInboxTagMutation,
  useGetTemplatesQuery,
  useGetTemplateVariablesQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  usePreviewTemplateMutation,
  useAiGenerateTemplateMutation,
  useGetBillingDashboardQuery,
  useGetPlansQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
  useGetSubscriptionQuery,
  useAssignSubscriptionMutation,
  useGetInvoicesQuery,
  usePayInvoiceMutation,
  useGetPaymentProfilesQuery,
  useAddPaymentProfileMutation,
  useSetDefaultCardMutation,
  useRemoveCardMutation,
  useGetPaymentsQuery,
  useManualPaymentMutation,
  useGetDocumentationQuery,
  useGetDocArticleQuery,
  useGetOnboardingQuery,
  useCreateDocCategoryMutation,
  useUpdateDocCategoryMutation,
  useDeleteDocCategoryMutation,
  useCreateDocArticleMutation,
  useUpdateDocArticleMutation,
  useDeleteDocArticleMutation,
  useGetCallsQuery,
  useGetCallQuery,
  usePlaceCallMutation,
  useGetVoicemailsQuery,
  useMarkVoicemailReadMutation,
} = platformApi;
